import type { AttendanceDay } from '../api/types';
import { daysInMonth } from './attendance-history';
import { namaLayak } from './display-name';
import type { Member, WorkerProfile } from './worker-candidates';

/**
 * Rekap absensi SEBULAN sebagai matriks orang × tanggal — MURNI, tanpa React dan tanpa jam sistem.
 *
 * ADA KARENA REKAP HARIAN TERLALU MAHAL DIPAKAI. Cetak harian (#890) dipakai Jihan sehari setelah
 * mendarat, dan batasnya langsung terasa: satu bulan berarti menekan cetak ~30 kali. Yang salah
 * periodenya, bukan tata letaknya — jadi modul ini hanya mengubah bentuk data; gaya cetak,
 * blok tanda tangan, dan penjaganya dipakai ulang.
 *
 * Satu sel = satu orang pada satu tanggal, diringkas menjadi SATU huruf. Itu bukan penghematan
 * ruang belaka: matriks 31 kolom hanya terbaca bila selnya dapat dipindai sekilas, dan angka jam
 * di tiap sel membuat halaman ini mustahil dibaca menyilang.
 */

/** Satu huruf per sel. `TANPA_CATATAN` sengaja bukan huruf supaya tidak terbaca sebagai status. */
export const KODE = {
  PRESENT: 'H',
  LATE: 'T',
  INCOMPLETE: 'B',
  ON_LEAVE: 'I',
  TANPA_CATATAN: '·',
} as const;

export type KodeHari = (typeof KODE)[keyof typeof KODE];

export const KETERANGAN_KODE: readonly { readonly kode: string; readonly arti: string }[] = [
  { kode: KODE.PRESENT, arti: 'Hadir' },
  { kode: KODE.LATE, arti: 'Terlambat' },
  { kode: KODE.INCOMPLETE, arti: 'Belum lengkap (tidak absen pulang)' },
  { kode: KODE.ON_LEAVE, arti: 'Izin / cuti' },
  { kode: KODE.TANPA_CATATAN, arti: 'Tidak ada catatan' },
];

export interface BarisBulanan {
  readonly userId: string;
  readonly name: string;
  /** Satu entri per tanggal dalam bulan itu, berurutan dari tanggal 1. */
  readonly sel: readonly KodeHari[];
  readonly hadir: number;
  readonly terlambat: number;
  readonly belumLengkap: number;
  readonly izin: number;
  /**
   * Tanggal TANPA catatan yang jatuh pada HARI KERJA saja.
   *
   * Akhir pekan tanpa catatan bukan ketidakhadiran, dan menghitungnya sebagai itu akan menuduh
   * setiap orang delapan kali sebulan. Kebalikannya juga dijaga: absen yang benar-benar terjadi
   * di akhir pekan TETAP muncul sebagai H/T di selnya — `work_days` hanya memberi makna pada
   * ringkasan, ia tidak pernah menolak check-in.
   */
  readonly tanpaCatatanHariKerja: number;
  readonly totalTelatMenit: number;
  /**
   * Hari yang tercatat TETAPI lokasinya tidak terverifikasi penuh.
   *
   * PINDAH KE SINI 31 Agu 2026, saat cetak rekap harian dihapus. Lembar harian dulu membawa
   * peringatan ini ke kertas dengan alasan yang eksplisit: manajer yang menandatangani rekap
   * berhak tahu sebelum menandatanganinya. Menghapus cetak harian tanpa memindahkan sinyalnya
   * akan membuang jaminan itu diam-diam — justru di lembar yang kini SATU-SATUNYA
   * ditandatangani.
   *
   * Hanya `false` yang dihitung. `undefined` berarti barisnya tidak membawa informasi itu, dan
   * menuduh berdasarkan ketiadaan data adalah cara laporan kehilangan kepercayaan.
   */
  readonly tanpaVerifikasiLokasi: number;
  /**
   * Hari yang tercatat TETAPI tidak lengkap selfienya (item 9, review 12 Sep 2026) — sinyal
   * TERPISAH dari lokasi, mencermin `tanpaVerifikasiLokasi` persis di atas: keduanya sengaja dua
   * pertanyaan berbeda yang kebetulan sering sama-sama benar.
   *
   * Hanya `false` yang dihitung, alasan yang sama dengan `tanpaVerifikasiLokasi`: `undefined`
   * berarti barisnya tidak membawa informasi itu (data lama sebelum kolom ini ada), bukan bukti
   * ketidaklengkapan.
   */
  readonly tanpaSelfie: number;
}

/** Seluruh tanggal `YYYY-MM-DD` dalam sebuah bulan `YYYY-MM`, berurutan. */
export function tanggalBulan(month: string): readonly string[] {
  const [tahunTeks, bulanTeks] = month.split('-');
  const tahun = Number(tahunTeks);
  const bulan = Number(bulanTeks);
  if (!Number.isInteger(tahun) || !Number.isInteger(bulan) || bulan < 1 || bulan > 12) return [];
  const jumlah = daysInMonth(tahun, bulan);
  return Array.from({ length: jumlah }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/**
 * Hari ke berapa dalam pekan (0 = Minggu), dibaca dari string tanggal.
 *
 * Dibangun DAN dibaca dalam UTC. `new Date('2026-08-31')` ditafsirkan sebagai UTC, tetapi
 * `getDay()` membacanya kembali di timezone browser — sehingga manajer di sebelah barat UTC
 * mendapat hari yang bergeser satu, dan kolom Sabtu-nya jatuh di Jumat. Hari dalam pekan adalah
 * aritmetika kalender, bukan urusan zona waktu (pola yang sama dengan `monthRange`).
 */
export function hariDalamPekan(workDate: string): number {
  const [t, b, h] = workDate.split('-').map(Number);
  return new Date(Date.UTC(t as number, (b as number) - 1, h as number)).getUTCDay();
}

/**
 * Benar bila tanggal itu hari kerja menurut kebijakan organisasi.
 *
 * `workDays` kosong atau tidak dikenal → SEMUA hari dianggap hari kerja. Itu disengaja: menebak
 * Senin–Jumat saat kebijakannya belum dibaca akan diam-diam memaafkan ketidakhadiran pada
 * organisasi yang benar-benar bekerja tujuh hari.
 */
export function hariKerja(workDate: string, workDays: readonly number[] | undefined): boolean {
  if (!workDays || workDays.length === 0) return true;
  return workDays.includes(hariDalamPekan(workDate));
}

function kodeDari(status: string | undefined): KodeHari {
  switch (status) {
    case 'PRESENT':
      return KODE.PRESENT;
    case 'LATE':
      return KODE.LATE;
    case 'INCOMPLETE':
      return KODE.INCOMPLETE;
    case 'ON_LEAVE':
      return KODE.ON_LEAVE;
    default:
      /*
       * Termasuk status yang LAHIR DI SERVER lebih dulu daripada di layar ini. Ia jatuh ke
       * "tanpa catatan", dan itu keliru — tetapi keliru yang terlihat: selnya kosong, bukan
       * barisnya hilang. Pola yang sama dengan `buildAttendanceRoster`.
       */
      return KODE.TANPA_CATATAN;
  }
}

/**
 * `'Lengkapi profil'`, bukan email — HBS pernah mengisi `displayName` dengan alamat email saat
 * profil belum dilengkapi (temuan review 12 Sep 2026 item 3: "bangdadi09@gmail.com" tercetak apa
 * adanya di lembar ini). Beda dari sapaan beranda yang cukup diam (`null`): lembar ini dicetak
 * dan ditandatangani, jadi barisnya harus tetap memberi tahu APA yang perlu dilakukan.
 */
function namaDari(members: readonly Member[], userId: string): string {
  const anggota = members.find((m) => m?.userId === userId);
  return namaLayak(anggota?.displayName) ?? 'Lengkapi profil';
}

/**
 * Matriks orang × tanggal untuk satu bulan.
 *
 * Hanya pekerja **aktif** yang muncul, sama seperti dashboard harian: yang sudah keluar tidak
 * diharapkan absen, dan memasukkannya membuat setiap laporan bulanan memanjang dengan baris yang
 * seluruhnya "tidak ada catatan".
 */
export function rekapBulanan(
  workers: readonly WorkerProfile[],
  members: readonly Member[],
  days: readonly AttendanceDay[],
  month: string,
  workDays: readonly number[] | undefined,
): readonly BarisBulanan[] {
  const tanggal = tanggalBulan(month);
  if (tanggal.length === 0) return [];

  // `userId|workDate` → status. Satu lintasan; pencarian bersarang pada 30 hari × N orang
  // menghasilkan kuadrat yang tidak perlu.
  const perOrangTanggal = new Map<string, AttendanceDay>();
  for (const hari of days) {
    if (typeof hari?.userId !== 'string' || typeof hari?.workDate !== 'string') continue;
    perOrangTanggal.set(`${hari.userId}|${hari.workDate}`, hari);
  }

  const baris: BarisBulanan[] = [];
  for (const pekerja of workers) {
    if (pekerja?.employmentStatus !== 'ACTIVE') continue;

    const sel: KodeHari[] = [];
    let hadir = 0;
    let terlambat = 0;
    let belumLengkap = 0;
    let izin = 0;
    let tanpaCatatanHariKerja = 0;
    let totalTelatMenit = 0;
    let tanpaVerifikasiLokasi = 0;
    let tanpaSelfie = 0;

    for (const tgl of tanggal) {
      const hari = perOrangTanggal.get(`${pekerja.userId}|${tgl}`);
      const kode = kodeDari(hari?.status);
      sel.push(kode);

      if (kode === KODE.PRESENT) hadir += 1;
      else if (kode === KODE.LATE) terlambat += 1;
      else if (kode === KODE.INCOMPLETE) belumLengkap += 1;
      else if (kode === KODE.ON_LEAVE) izin += 1;
      else if (hariKerja(tgl, workDays)) tanpaCatatanHariKerja += 1;

      if (typeof hari?.lateMinutes === 'number' && hari.lateMinutes > 0) {
        totalTelatMenit += hari.lateMinutes;
      }
      if (hari?.isLocationVerified === false) tanpaVerifikasiLokasi += 1;
      if (hari?.isSelfieComplete === false) tanpaSelfie += 1;
    }

    baris.push({
      userId: pekerja.userId,
      name: namaDari(members, pekerja.userId),
      sel,
      hadir,
      terlambat,
      belumLengkap,
      izin,
      tanpaCatatanHariKerja,
      totalTelatMenit,
      tanpaVerifikasiLokasi,
      tanpaSelfie,
    });
  }

  // Diurutkan nama supaya lembar bulan ini dan bulan lalu dapat ditumpuk dan dibandingkan baris
  // per baris. Urutan yang mengikuti data akan bergeser setiap kali ada orang baru.
  return baris.sort((a, b) => a.name.localeCompare(b.name, 'id'));
}
